import React from 'react';
import { useNavigate } from 'react-router-dom';
import { googleLogout } from '@react-oauth/google';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';

import { useProfileQuery } from '../actions/apiSlice';

const ProfileScreen = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('aircheck_access');
  const { data: profile, isLoading } = useProfileQuery(undefined, { skip: !token });

  const handleLogout = () => {
    localStorage.removeItem('aircheck_access');
    localStorage.removeItem('aircheck_refresh');
    googleLogout();
    navigate('/login');
  };

  const initial = profile?.username ? profile.username[0].toUpperCase() : '?';

  return (
    <>
      <Helmet>
        <title>Profile | AirCheck</title>
      </Helmet>
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4.5 },
            borderRadius: 4,
            border: (t) => `1px solid ${t.palette.divider}`,
            textAlign: 'center',
          }}
        >
          <Avatar
            sx={{
              width: 110,
              height: 110,
              mx: 'auto',
              mb: 2,
              fontSize: 44,
              fontWeight: 700,
              background: 'linear-gradient(135deg,#E50914,#FF4D5E)',
            }}
          >
            {token ? initial : '?'}
          </Avatar>

          {token ? (
            <>
              {isLoading ? (
                <Skeleton variant="text" width={180} sx={{ mx: 'auto', fontSize: 28 }} />
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {profile?.username}
                </Typography>
              )}
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {profile?.email || 'AirCheck member'}
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<BookmarkBorderIcon />}
                  onClick={() => navigate('/watchlist')}
                >
                  My watchlist
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  startIcon={<NotificationsActiveIcon />}
                  onClick={() => navigate('/reminder')}
                  sx={{ borderColor: (t) => t.palette.divider }}
                >
                  My reminders
                </Button>
                <Button
                  variant="text"
                  color="error"
                  size="large"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                >
                  Sign out
                </Button>
              </Stack>
            </>
          ) : (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                You're not signed in
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Sign in to manage your watchlist and reminders.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<LoginIcon />}
                onClick={() => navigate('/login')}
              >
                Sign in
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ProfileScreen;
